import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Tooltip 
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Add as AddIcon 
} from '@mui/icons-material';

import FieldWithModificationIndicator from './FieldWithModificationIndicator';
import LocalTextbelegeField from './LocalTextbelegeField';

// Debounce-Funktion
const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

interface TextbelegeEditorProps {
  anforderungNummer: number;
  originalTextbelege: any[];
  canEdit: boolean;
  onTextbelegeChange: (anforderungNummer: number, textbelege: any[]) => void;
  isFieldModified?: (anforderungNummer: number, belegIndex: number, field: string) => boolean;
}

// Optimierte Textbelege-Komponente
const TextbelegeEditor = React.memo<TextbelegeEditorProps>(({ 
  anforderungNummer, 
  originalTextbelege, 
  canEdit, 
  onTextbelegeChange, 
  isFieldModified 
}) => {
  const [localTextbelege, setLocalTextbelege] = useState<any[]>(originalTextbelege);

  // Update lokale Textbelege wenn sich originalTextbelege ändert
  useEffect(() => {
    setLocalTextbelege(originalTextbelege);
  }, [originalTextbelege]);

  // Debounced Change für Parent-Update
  const debouncedChange = useCallback(
    debounce((newTextbelege: any[]) => {
      onTextbelegeChange(anforderungNummer, newTextbelege);
    }, 300),
    [anforderungNummer, onTextbelegeChange]
  );

  // Textbelege-Feld ändern mit lokaler State-Optimierung
  const handleFieldChange = useCallback((belegIndex: number, field: string, value: string) => {
    const updatedTextbelege = [...localTextbelege];
    updatedTextbelege[belegIndex] = {
      ...updatedTextbelege[belegIndex],
      [field]: value
    };
    setLocalTextbelege(updatedTextbelege); // Sofort lokale UI-Update
    debouncedChange(updatedTextbelege); // Debounced Parent-Update
  }, [localTextbelege, debouncedChange]);

  // Textbelege löschen
  const handleDelete = useCallback((belegIndex: number) => {
    const newTextbelege = localTextbelege.filter((_, index) => index !== belegIndex);
    setLocalTextbelege(newTextbelege); // Sofort lokale UI-Update
    debouncedChange(newTextbelege); // Debounced Parent-Update
  }, [localTextbelege, debouncedChange]);

  // Neue Textbelege hinzufügen
  const handleAdd = useCallback(() => {
    const newTextbelege = [...localTextbelege, {
      belegtyp: "normal",
      dokumenttyp: "",
      quellenangabe: "",
      textauszug: "",
      relevanz: ""
    }];
    setLocalTextbelege(newTextbelege); // Sofort lokale UI-Update
    debouncedChange(newTextbelege); // Debounced Parent-Update
  }, [localTextbelege, debouncedChange]);

  return (
    <>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        <strong>Textbelege ({localTextbelege.length}):</strong>
      </Typography>
      {localTextbelege.map((beleg: any, belegIndex: number) => (
        <Box key={belegIndex} sx={{ mb: 1, p: 1, backgroundColor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="caption" color="textSecondary">
              <strong>Textbeleg {belegIndex + 1}</strong>
            </Typography>
            {canEdit && (
              <Tooltip title="Textbeleg löschen">
                <IconButton
                  size="small"
                  onClick={() => handleDelete(belegIndex)}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          
          <FieldWithModificationIndicator 
            isModified={isFieldModified ? isFieldModified(anforderungNummer, belegIndex, 'quellenangabe') : false}
            label="Quelle"
          >
            <LocalTextbelegeField
              label="Quelle"
              initialValue={beleg.quellenangabe || ''}
              belegIndex={belegIndex}
              field="quellenangabe"
              disabled={!canEdit}
              onFieldChange={handleFieldChange}
            />
          </FieldWithModificationIndicator>
          
          <FieldWithModificationIndicator 
            isModified={isFieldModified ? isFieldModified(anforderungNummer, belegIndex, 'textauszug') : false}
            label="Textauszug"
          >
            <LocalTextbelegeField
              label="Textauszug"
              initialValue={beleg.textauszug || ''}
              belegIndex={belegIndex}
              field="textauszug"
              disabled={!canEdit}
              multiline
              rows={3}
              onFieldChange={handleFieldChange}
            />
          </FieldWithModificationIndicator>
          
          <FieldWithModificationIndicator 
            isModified={isFieldModified ? isFieldModified(anforderungNummer, belegIndex, 'relevanz') : false}
            label="Relevanz"
          >
            <LocalTextbelegeField
              label="Relevanz"
              initialValue={beleg.relevanz || ''}
              belegIndex={belegIndex}
              field="relevanz"
              disabled={!canEdit}
              onFieldChange={handleFieldChange}
            />
          </FieldWithModificationIndicator>
        </Box>
      ))}
      
      {canEdit && (
        <Typography
          onClick={handleAdd}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '1rem', my: 1 }}
        >
          <AddIcon /> Textbeleg hinzufügen
        </Typography>
      )}
    </>
  );
});

TextbelegeEditor.displayName = 'TextbelegeEditor';

export default TextbelegeEditor;